import { useCallback, useEffect, useState } from "react";
import { outreachApi } from "@/lib/api";

/** Matches backend default until GET /outreach/campaigns/timezones loads. */
export const FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE = "Asia/Calcutta";

/** @typedef {{ iana: string, name: string, gmt: string }} TimezoneDisplay */
/** @typedef {{ defaultIana: string, zones: TimezoneDisplay[], byIana: Record<string, TimezoneDisplay> }} TimezoneCatalog */

/** @type {TimezoneCatalog | null} */
let cachedCatalog = null;
/** @type {Promise<TimezoneCatalog> | null} */
let inflight = null;

/** @param {object} data */
function normalizeCatalog(data) {
  const zones = Array.isArray(data?.zones) ? data.zones : [];
  const byIana = data?.byIana && typeof data.byIana === "object"
    ? data.byIana
    : Object.fromEntries(zones.map((z) => [z.iana, z]));
  return {
    defaultIana: data?.defaultIana || FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE,
    zones,
    byIana,
  };
}

function fetchCatalog(extraIana) {
  const hasCache = cachedCatalog && !extraIana;
  if (hasCache) return Promise.resolve(cachedCatalog);
  if (!inflight || extraIana) {
    inflight = outreachApi
      .listCampaignTimezones(extraIana || undefined)
      .then((data) => {
        const next = normalizeCatalog(data);
        if (cachedCatalog && extraIana) {
          cachedCatalog = {
            ...cachedCatalog,
            zones: next.zones,
            byIana: { ...cachedCatalog.byIana, ...next.byIana },
          };
        } else {
          cachedCatalog = next;
        }
        return cachedCatalog;
      })
      .catch((err) => {
        inflight = null;
        throw err;
      });
  }
  return inflight;
}

/** Cached catalog + display rows from the backend (`zones`, `byIana`, `defaultIana`). */
export function useCampaignTimezoneCatalog() {
  const [catalog, setCatalog] = useState(cachedCatalog);
  const [loading, setLoading] = useState(!cachedCatalog);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cachedCatalog) {
      setCatalog(cachedCatalog);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    fetchCatalog()
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load timezones");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultIana = (catalog?.defaultIana || "").trim() || FALLBACK_DEFAULT_CAMPAIGN_TIMEZONE;

  const displayFor = useCallback(
    (iana) => {
      const key = (iana || "").trim();
      if (!key) return null;
      return catalog?.byIana?.[key] || null;
    },
    [catalog],
  );

  const ensureDisplay = useCallback(async (iana) => {
    const key = (iana || "").trim();
    if (!key) return null;
    if (catalog?.byIana?.[key]) return catalog.byIana[key];
    try {
      const data = await fetchCatalog(key);
      setCatalog(data);
      return data.byIana[key] || null;
    } catch {
      return null;
    }
  }, [catalog]);

  return { catalog, defaultIana, loading, error, displayFor, ensureDisplay };
}

/** @visibleForTesting */
export function __resetCampaignTimezoneCatalogCacheForTests() {
  cachedCatalog = null;
  inflight = null;
}
